import { inject } from '@angular/core';
import { HttpTransportType, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of, tap } from 'rxjs';

import { switchMapToVoid, VOID } from '@ideals/utils/void';

import { DiagnosticStatus, ErrorDetails } from '../../models';

import { Troubleshooting } from './troubleshooting';

const CONNECTION_TIMEOUT = 10000;

export class CheckWebSocketService extends Troubleshooting {
  readonly #translateService = inject(TranslateService);

  #errorReport: string = '';
  #status: DiagnosticStatus = 'Default';

  override getErrorDetails(): ErrorDetails {
    const title = this.#translateService.instant('help_modal.TEXT.Websocket_failed') as string;
    const description = this.#translateService.instant('help_modal.TEXT.We_ran_websocket_test', {
      serverName: window.location.hostname,
    }) as string;

    return {
      title,
      description,
      dataIdeals: 'copy-web-socket-issues',
    };
  }

  override getErrorReports(): { [key: string]: string; } {
    return { websockets: this.#errorReport };
  }

  override getStatus(): DiagnosticStatus {
    return this.#status;
  }

  override run(): Observable<void> {
    this.#status = 'InProgress';

    return this.#checkConnection().pipe(
      tap(() => {
        this.#status = 'Completed';
        this.#errorReport = '';
      }),
      catchError((error: Error) => {
        this.#status = 'Error';
        this.#errorReport = error.message;

        return of(VOID);
      }),
      switchMapToVoid,
    );
  }

  #checkConnection(): Observable<void> {
    return new Observable<void>((subscriber) => {
      const connection = new HubConnectionBuilder()
        .withUrl(`${window.location.origin}/api/room-copy/connectivity`, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .build();

      const connectionTimeout = setTimeout(() => {
        connection.stop();
        subscriber.error(new Error('Connection timeout'));
      }, CONNECTION_TIMEOUT);

      connection.start()
        .then(() => {
          clearTimeout(connectionTimeout);

          connection.stop().finally(() => {
            subscriber.next();
            subscriber.complete();
          });
        })
        .catch((error: Error) => {
          clearTimeout(connectionTimeout);

          subscriber.error(error);
        });

      return () => {
        clearTimeout(connectionTimeout);

        if (connection.state !== HubConnectionState.Disconnected) {
          connection.stop();
        }
      };
    });
  }
}
